import React from 'react';
import { RotateCw, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

/**
 * Live indexing badge.
 *
 * Reads `indexing_progress` from the retrieval API. Renders:
 *  - phase label in Spanish ("Preparando", "Explorando archivos", "Comparando…", "Indexando", "Finalizando")
 *  - a true progress bar
 *  - "X de N archivos · M chunks" counters
 *  - relative "hace Ns" so the user can see the worker is actually moving
 *  - a "atascado" warning when no progress for >60s, with a "reanudar" button
 *  - a red error state with the actual reason when indexing_state === 'error'
 *  - a stable green "RAG · N chunks / N archivos" badge when ready
 */
export interface IndexProgress {
  job_id?: string;
  phase?: string;
  phase_label?: string;
  files_total?: number;
  files_to_index?: number;
  files_processed?: number;
  files_already_indexed?: number;
  chunks_so_far?: number;
  chunks_in_batch?: number;
  chunks_embedded_in_batch?: number;
  batch?: number;
  total_batches?: number;
  percent?: number;
  last_update_at?: string;
  final_report?: {
    ok: boolean;
    error?: string | null;
    files_indexed: number;
    chunks_total: number;
    chunks_inserted?: number;
    duration_ms: number;
    completed_at?: string;
  } | null;
}

export interface IndexStatus {
  chunks: number;
  files_indexed: number;
  model?: string;
  dim?: number;
  indexing_state?: 'running' | 'scheduled' | 'ready' | 'error' | null;
  indexing_error?: string | null;
  indexing_started_at?: string | null;
  indexing_completed_at?: string | null;
  indexing_job_id?: string | null;
  indexing_progress?: IndexProgress | null;
}

function relativeFrom(iso?: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 1) return 'ahora';
  if (s < 60) return `hace ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  return `hace ${h}h`;
}

interface Props {
  status: IndexStatus;
  reindexing: boolean;
  onReindex: () => void;
}

export const IndexingBadge: React.FC<Props> = ({ status, reindexing, onReindex }) => {
  // Tick once per second so "hace Ns" updates and stuck detection works.
  const [now, setNow] = React.useState<number>(Date.now());
  React.useEffect(() => {
    const isBusy = status.indexing_state === 'running' || status.indexing_state === 'scheduled';
    if (!isBusy) return;
    const h = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(h);
  }, [status.indexing_state]);

  const progress = status.indexing_progress;
  const isBusy = status.indexing_state === 'running' || status.indexing_state === 'scheduled';
  const isError = status.indexing_state === 'error';
  const isReady = !isBusy && !isError && status.chunks > 0;

  // Stuck detection: when busy AND last_update_at hasn't moved in 60s.
  let stuckSeconds = 0;
  if (isBusy && progress?.last_update_at) {
    const t = new Date(progress.last_update_at).getTime();
    if (!Number.isNaN(t)) {
      stuckSeconds = Math.floor((now - t) / 1000);
    }
  }
  const isStuck = stuckSeconds >= 60;

  const percent = Math.max(0, Math.min(100, progress?.percent ?? 0));

  // Color logic
  const dotColor = isError
    ? 'bg-red-500'
    : isStuck
      ? 'bg-amber-500'
      : isBusy
        ? 'bg-amber-400 animate-pulse'
        : isReady
          ? 'bg-emerald-400'
          : 'bg-zinc-500';

  const headerLabel = isError
    ? 'Error al indexar'
    : isBusy
      ? progress?.phase_label || 'Indexando'
      : isReady
        ? `RAG · ${status.chunks} chunks / ${status.files_indexed} archivos`
        : 'Sin índice';

  // Counters string (shown only while busy or stuck)
  let counters = '';
  if (isBusy || isStuck) {
    if (progress?.phase === 'processing' && progress?.files_to_index) {
      const embedded = progress.chunks_embedded_in_batch ?? 0;
      const inBatch = progress.chunks_in_batch ?? 0;
      const ai = progress.files_already_indexed ?? 0;
      const subBatchHint =
        inBatch > 0 && embedded > 0 && embedded < inBatch
          ? ` · embebiendo ${embedded}/${inBatch} del lote`
          : '';
      const aiHint = ai > 0 ? ` · ${ai} ya indexados` : '';
      counters =
        `${progress.files_processed ?? 0}/${progress.files_to_index} archivos · ` +
        `${progress.chunks_so_far ?? 0} chunks${subBatchHint}${aiHint}`;
    } else if (progress?.phase === 'reading') {
      const t = progress.files_total ?? 0;
      const p = progress.files_processed ?? 0;
      const ai = progress.files_already_indexed ?? 0;
      if (t > 0) {
        counters = ai > 0
          ? `${p}/${t} archivos leídos · ${ai} ya indexados`
          : `${p}/${t} archivos leídos`;
      } else {
        counters = 'Leyendo archivos';
      }
    } else if (progress?.phase === 'walking') {
      counters = 'Recorriendo árbol';
    } else if (progress?.phase === 'diffing') {
      counters = 'Detectando cambios';
    } else if (progress?.phase === 'preparing') {
      counters = 'Inicializando worker';
    } else if (progress?.phase === 'finalizing') {
      counters = 'Cerrando índice';
    } else if (status.indexing_state === 'scheduled') {
      counters = 'En cola';
    }
  }

  return (
    <div
      className="flex flex-col gap-1.5 text-xs px-3 py-2 border border-white/10 rounded-md bg-black/30 min-w-[280px]"
      data-testid="retrieval-index-badge"
      title={
        isError
          ? `Error: ${status.indexing_error}`
          : `Modelo: ${status.model || 'n/a'} · ${status.dim || 0}d`
      }
    >
      <div className="flex items-center gap-2">
        <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
        {isError ? (
          <AlertTriangle className="w-3 h-3 text-red-400 shrink-0" />
        ) : isBusy && !isStuck ? (
          <Loader2 className="w-3 h-3 text-amber-300 animate-spin shrink-0" />
        ) : isReady ? (
          <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
        ) : isStuck ? (
          <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
        ) : null}
        <span
          className="text-white font-mono truncate flex-1"
          data-testid="retrieval-index-label"
        >
          {headerLabel}
        </span>
        <button
          type="button"
          disabled={reindexing}
          onClick={onReindex}
          data-testid="retrieval-reindex-btn"
          title={isStuck ? 'Reanudar indexación' : 'Reindexar'}
          className="text-[#A1A1AA] hover:text-white disabled:opacity-50"
        >
          <RotateCw className={`w-3 h-3 ${reindexing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Progress bar — visible while busy OR right after completion */}
      {(isBusy || isStuck) && (
        <>
          <div
            className="h-1 w-full bg-white/5 rounded overflow-hidden"
            data-testid="retrieval-progress-bar"
          >
            <div
              className={`h-full transition-all duration-500 ${
                isStuck ? 'bg-amber-500' : 'bg-emerald-500/80'
              }`}
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono text-[#A1A1AA]">
            <span data-testid="retrieval-progress-counters">{counters}</span>
            <span data-testid="retrieval-progress-meta">
              {isStuck ? (
                <span className="text-amber-400">sin avance {stuckSeconds}s</span>
              ) : progress?.batch && progress?.total_batches ? (
                <>
                  lote {progress.batch}/{progress.total_batches} · {percent}% ·{' '}
                  {relativeFrom(progress?.last_update_at)}
                </>
              ) : (
                <>{percent}% · {relativeFrom(progress?.last_update_at)}</>
              )}
            </span>
          </div>
        </>
      )}

      {/* Final report — only when state==ready AND final_report present */}
      {isReady && progress?.final_report?.ok && (
        <div
          className="text-[10px] font-mono text-emerald-300/90 break-words"
          data-testid="retrieval-final-report"
        >
          {`Indexación completada · ${progress.final_report.files_indexed} archivos · ` +
            `${progress.final_report.chunks_total} chunks · ` +
            `${(progress.final_report.duration_ms / 1000).toFixed(1)}s`}
        </div>
      )}

      {/* Error block — actual reason from backend */}
      {isError && (
        <div
          className="text-[10px] font-mono text-red-300/90 break-words"
          data-testid="retrieval-index-error"
        >
          {status.indexing_error || 'falla desconocida'}
        </div>
      )}

      {/* Stuck banner — actionable */}
      {isStuck && (
        <div
          className="text-[10px] text-amber-300/90"
          data-testid="retrieval-index-stuck"
        >
          El proceso no avanza desde hace {stuckSeconds}s. Pulsa ↻ para reanudarlo.
        </div>
      )}
    </div>
  );
};

export default IndexingBadge;
